import styled from 'styled-components'
import { rem } from 'polished'
import { defineMessages, useIntl } from 'react-intl'

import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import LineRestaurantIcon from 'LuxKit/Icons/line/LineRestaurantIcon'
import LineShoppingBagIcon from 'LuxKit/Icons/line/LineShoppingBagIcon'

type EmptyStateVariant = 'timeline' | 'list'
type ListItemTone = 'dining' | 'accommodation' | 'shopping'

interface Props {
  variant: EmptyStateVariant
}

const messages = defineMessages({
  timelineHeading: {
    id: 'el0Ox_-wET9mv89AWfdA',
    defaultMessage: 'Your itinerary will take shape here',
    description: 'Empty state heading on the trip planner timeline panel before any itinerary is created',
  },
  listHeading: {
    id: 'v2-EgnmUkGHxZKThE714',
    defaultMessage: 'Your trip at a glance',
    description: 'Empty state heading on the trip planner list panel before any trip items are created',
  },
  timelineBody: {
    id: 'qQevy9oRd02wFAz8xoR3',
    defaultMessage: 'As we chat, your trip will be arranged day-by-day with suggested moments, routes, and experiences you can rearrange anytime.',
    description: 'Empty state body text on the trip planner timeline panel before any itinerary is created',
  },
  listBody: {
    id: 'YYeF9YFVxBGqAKzkWeTY',
    defaultMessage: 'Hotels, experiences, flights: everything you add will appear here in one place, ready to sort and refine.',
    description: 'Empty state body text on the trip planner list panel before any trip items are created',
  },
  firstDayLabel: {
    id: 'IYqU0YST5o4i23yzg5C_',
    defaultMessage: 'Day 1',
    description: 'First day label in the trip planner timeline empty state illustration',
  },
  secondDayLabel: {
    id: 'uzYrmKcuUzFNXir8_TNl',
    defaultMessage: 'Day 2',
    description: 'Second day label in the trip planner timeline empty state illustration',
  },
  thirdDayLabel: {
    id: 'CZTHuzoeW3nErvrCRbIN',
    defaultMessage: 'Day 3',
    description: 'Third day label in the trip planner timeline empty state illustration',
  },
})

const EmptyStateContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: min(100%, ${rem(500)});
`

const EmptyStateCopy = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;
`

const TimelinePreview = styled.div`
  width: min(100%, ${rem(398)});
  display: grid;
  grid-template-columns: repeat(3, ${rem(58)});
  justify-content: space-between;
  position: relative;
  margin-bottom: ${rem(54)};

  &::before {
    content: '';
    position: absolute;
    top: ${rem(21)};
    inset-inline: ${rem(52)};
    border-top: ${rem(2)} dashed ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const TimelineDay = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(2)};
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  position: relative;
  z-index: 1;

  &.is-active {
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
  }
`

const TimelineDot = styled.div`
  width: ${rem(44)};
  height: ${rem(44)};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};

  &::after {
    content: '';
    width: ${rem(14)};
    height: ${rem(14)};
    border-radius: ${props => props.theme.borderRadius.round};
    background: ${props => props.theme.palette.neutral.surface.lightForeground};
  }
`

const ActiveTimelineDot = styled(TimelineDot)`
  background: ${props => props.theme.palette.highlight.primary.darkBackground};

  &::after {
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
  }
`

const TimelineLabel = styled(BodyText)`
  color: currentColor;
`

const ListPreview = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(26)};
  width: min(100%, ${rem(260)});
  margin-bottom: ${rem(40)};
`

const ListPreviewRow = styled.div`
  --list-preview-line-background: ${props => `color-mix(in srgb, ${props.theme.palette.neutral.surface.lightForeground} 72%, ${props.theme.palette.neutral.surface.normalBackground})`};
  --list-preview-line-highlight: ${props => `color-mix(in srgb, ${props.theme.palette.neutral.surface.lightForeground} 54%, ${props.theme.palette.neutral.surface.normalBackground})`};

  display: grid;
  grid-template-columns: ${rem(52.66)} 1fr;
  column-gap: ${rem(16.46)};
  align-items: center;

  &:nth-child(2) {
    --list-preview-line-background: ${props => `color-mix(in srgb, ${props.theme.palette.neutral.surface.lightForeground} 54%, ${props.theme.palette.neutral.surface.normalBackground})`};
    --list-preview-line-highlight: ${props => `color-mix(in srgb, ${props.theme.palette.neutral.surface.lightForeground} 36%, ${props.theme.palette.neutral.surface.normalBackground})`};
  }

  &:nth-child(3) {
    --list-preview-line-background: ${props => `color-mix(in srgb, ${props.theme.palette.neutral.surface.lightForeground} 36%, ${props.theme.palette.neutral.surface.normalBackground})`};
    --list-preview-line-highlight: ${props => `color-mix(in srgb, ${props.theme.palette.neutral.surface.lightForeground} 20%, ${props.theme.palette.neutral.surface.normalBackground})`};
  }
`

const ListPreviewIcon = styled.div<{ $tone: ListItemTone }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(52.66)};
  height: ${rem(52.66)};
  padding: ${rem(13.16)};
  border-radius: ${rem(13.16)};

  ${props => props.$tone === 'dining' && `
    background: color-mix(in srgb, ${props.theme.palette.messaging.warning.lightBackground} 56%, ${props.theme.palette.neutral.surface.normalBackground});
    color: ${props.theme.palette.messaging.warning.darkForeground};
  `}

  ${props => props.$tone === 'accommodation' && `
    background: color-mix(in srgb, ${props.theme.palette.highlight.secondary.lightBackground} 64%, ${props.theme.palette.neutral.surface.normalBackground});
    color: ${props.theme.palette.highlight.secondary.normalForeground};
  `}

  ${props => props.$tone === 'shopping' && `
    background: color-mix(in srgb, ${props.theme.palette.messaging.critical.lightBackground} 24%, ${props.theme.palette.neutral.surface.normalBackground});
    color: ${props.theme.palette.messaging.critical.darkForeground};
  `}
`

const ListPreviewLines = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(10)};
`

const ListPreviewLine = styled.div`
  height: ${rem(11)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: linear-gradient(
    90deg,
    var(--list-preview-line-background) 0%,
    var(--list-preview-line-highlight) 48%,
    var(--list-preview-line-background) 100%
  );

  &:first-child {
    width: 100%;
  }

  &:last-child {
    width: 64%;
  }
`

interface TimelineEmptyStatePreviewProps {
  dayLabels: [string, string, string]
}

function TimelineEmptyStatePreview({ dayLabels }: TimelineEmptyStatePreviewProps) {
  return (
    <TimelinePreview aria-hidden="true">
      <TimelineDay className="is-active">
        <ActiveTimelineDot />
        <TimelineLabel variant="large" weight="bold">{dayLabels[0]}</TimelineLabel>
      </TimelineDay>
      <TimelineDay>
        <TimelineDot />
        <TimelineLabel variant="large" weight="bold">{dayLabels[1]}</TimelineLabel>
      </TimelineDay>
      <TimelineDay>
        <TimelineDot />
        <TimelineLabel variant="large" weight="bold">{dayLabels[2]}</TimelineLabel>
      </TimelineDay>
    </TimelinePreview>
  )
}

function ListEmptyStatePreview() {
  return (
    <ListPreview aria-hidden="true">
      <ListPreviewRow>
        <ListPreviewIcon $tone="dining">
          <LineRestaurantIcon size="M" />
        </ListPreviewIcon>
        <ListPreviewLines>
          <ListPreviewLine />
          <ListPreviewLine />
        </ListPreviewLines>
      </ListPreviewRow>
      <ListPreviewRow>
        <ListPreviewIcon $tone="accommodation">
          <LineBedIcon size="M" />
        </ListPreviewIcon>
        <ListPreviewLines>
          <ListPreviewLine />
          <ListPreviewLine />
        </ListPreviewLines>
      </ListPreviewRow>
      <ListPreviewRow>
        <ListPreviewIcon $tone="shopping">
          <LineShoppingBagIcon size="M" />
        </ListPreviewIcon>
        <ListPreviewLines>
          <ListPreviewLine />
          <ListPreviewLine />
        </ListPreviewLines>
      </ListPreviewRow>
    </ListPreview>
  )
}

function RightPanelEmptyState({ variant }: Props) {
  const intl = useIntl()
  const isTimeline = variant === 'timeline'

  const heading = intl.formatMessage(isTimeline ? messages.timelineHeading : messages.listHeading)
  const body = intl.formatMessage(isTimeline ? messages.timelineBody : messages.listBody)

  const dayLabels: [string, string, string] = [
    intl.formatMessage(messages.firstDayLabel),
    intl.formatMessage(messages.secondDayLabel),
    intl.formatMessage(messages.thirdDayLabel),
  ]

  return (
    <EmptyStateContent>
      {isTimeline ? <TimelineEmptyStatePreview dayLabels={dayLabels} /> : <ListEmptyStatePreview />}
      <EmptyStateCopy>
        <Heading variant="heading3" align="center" as="h2">{heading}</Heading>
        <BodyText variant="medium" colour="light-foreground-alt" align="center">
          {body}
        </BodyText>
      </EmptyStateCopy>
    </EmptyStateContent>
  )
}

export default RightPanelEmptyState
